import { useEffect, useState } from 'react'
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { Bookmark, Home, Map, Menu, MessageCircle, Search, HandCoins, Settings, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAuth } from '@/hooks/use-auth'
import { useMediaQuery } from '@/hooks/use-media-query'
import { openSignIn } from '@/lib/signin-navigation'
import { openSettings } from '@/lib/settings-navigation'
import { Spinner } from '@/components/ui/spinner'
import { MapsProvider } from '@/components/maps/MapsProvider'
import { MapPage } from '@/pages/MapPage'
import { BiddleDetailModal } from '@/components/biddle/BiddleDetailModal'
import { PropertyProfileModal } from '@/components/property/PropertyProfileModal'
import biddleLogoWhite from '@/assets/biddle-logo-white.png'
import biddleLogo from '@/assets/biddle-logo.png'

const NAV_CLOSE_MS = 300
/** Desktop push-rail width (Tailwind w-64). Keep in sync with aside/inner classes. */
const DESKTOP_RAIL_OPEN = 'w-64'
const DESKTOP_RAIL_INNER = 'w-64'

/**
 * Navigation chrome model
 * -----------------------
 * Desktop (≥ md):
 *   - Collapsible left rail. Closed by default; hamburger opens it.
 *   - Stays open across tab changes. Closes only via the X in the rail.
 *   - Search: hamburger + Sign in float over the page (no top bar).
 * Mobile (< md):
 *   - Hamburger opens an overlay drawer (slide in/out).
 *   - Link taps wait for the slide-out before navigating. Logo in the top bar.
 */

const tabs = [
  { to: '/search', label: 'Search', icon: Search },
  { to: '/map', label: 'Map', icon: Map },
  { to: '/biddles', label: 'Biddles', icon: HandCoins },
  { to: '/collections', label: 'Collections', icon: Bookmark },
  { to: '/properties', label: 'Properties', icon: Home },
  { to: '/chat', label: 'Messages', icon: MessageCircle },
]

export function FoundationShell() {
  const { user, profile, isGuest, isLoading, logout, isSigningOut } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const isDesktop = useMediaQuery('(min-width: 768px)')
  const [navOpen, setNavOpen] = useState(false)
  /** Mobile overlay mount + enter flags (desktop uses a push rail, no overlay). */
  const [drawerMounted, setDrawerMounted] = useState(false)
  const [drawerEntered, setDrawerEntered] = useState(false)

  const isSearchRoute = location.pathname === '/search'
  const isMapRoute = location.pathname === '/map'
  const isChatRoute = location.pathname === '/chat' || location.pathname.startsWith('/chat/')

  const accountLabel = isLoading
    ? '…'
    : user && !isGuest
      ? profile?.displayName || profile?.email || user.email || 'Signed in'
      : 'Browsing as guest'

  async function onSignOut() {
    await logout()
    navigate('/search', { replace: true })
  }

  function onSignIn() {
    openSignIn(navigate, location, location.pathname === '/signin' ? '/search' : location.pathname)
  }

  function onOpenSettings() {
    if (isDesktop) {
      openSettings(navigate, location)
      return
    }
    closeNavThen(() => openSettings(navigate, location))
  }

  // Keep the map mounted after first visit so camera, pins, and search persist.
  const [mapMounted, setMapMounted] = useState(isMapRoute)
  useEffect(() => {
    if (isMapRoute) setMapMounted(true)
  }, [isMapRoute])

  // Mobile overlay drawer enter/exit animation. Desktop rail is always mounted
  // and toggled with width — no mount thrash, no route-coupled animation.
  useEffect(() => {
    if (isDesktop) {
      setDrawerMounted(false)
      setDrawerEntered(false)
      return
    }
    if (navOpen) {
      setDrawerMounted(true)
      const id = window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => setDrawerEntered(true))
      })
      return () => window.cancelAnimationFrame(id)
    }
    setDrawerEntered(false)
    const timer = window.setTimeout(() => setDrawerMounted(false), NAV_CLOSE_MS)
    return () => window.clearTimeout(timer)
  }, [navOpen, isDesktop])

  function closeNavThen(action: () => void) {
    if (!navOpen && !drawerMounted) {
      action()
      return
    }
    setNavOpen(false)
    window.setTimeout(action, NAV_CLOSE_MS)
  }

  function goTo(to: string) {
    if (location.pathname === to) {
      // Same tab: on mobile, dismiss the overlay so content is usable.
      if (!isDesktop) setNavOpen(false)
      return
    }

    // Desktop: navigate immediately; leave the rail open until the user hits X.
    if (isDesktop) {
      navigate(to)
      return
    }

    // Mobile: finish slide-out, then navigate.
    closeNavThen(() => navigate(to))
  }

  function NavLinks({ viaDrawer }: { viaDrawer?: boolean }) {
    return (
      <>
        {tabs.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            onClick={
              viaDrawer
                ? (e) => {
                    e.preventDefault()
                    goTo(to)
                  }
                : undefined
            }
            className={({ isActive }) =>
              cn(
                'flex items-center rounded-xl font-medium text-white/80 transition hover:bg-white/10 hover:text-white',
                isDesktop
                  ? 'gap-3.5 px-4 py-3 text-base'
                  : 'gap-3 px-3 py-2.5 text-sm',
                isActive && 'bg-white/15 text-white',
              )
            }
          >
            <Icon className={isDesktop ? 'h-5 w-5' : 'h-4 w-4'} />
            {label}
          </NavLink>
        ))}
        <button
          type="button"
          onClick={onOpenSettings}
          className={cn(
            'mt-auto flex w-full items-center rounded-xl text-left font-medium text-white/80 transition hover:bg-white/10 hover:text-white',
            isDesktop
              ? 'gap-3.5 px-4 py-3 text-base'
              : 'gap-3 px-3 py-2.5 text-sm',
            location.pathname === '/settings' && 'bg-white/15 text-white',
          )}
        >
          <Settings className={isDesktop ? 'h-5 w-5' : 'h-4 w-4'} />
          Settings
        </button>
      </>
    )
  }

  function AccountFooter() {
    return (
      <div
        className={cn(
          'shrink-0 border-t border-white/10 text-white/60',
          isDesktop ? 'px-7 py-5 text-sm' : 'px-6 py-4 text-xs',
        )}
      >
        <div className="truncate text-white/80">{accountLabel}</div>
        {user ? (
          <button
            type="button"
            disabled={isSigningOut}
            onClick={() => void onSignOut()}
            className="mt-2 text-biddle-blue hover:underline disabled:opacity-50"
          >
            {isSigningOut ? 'Signing out…' : 'Sign out'}
          </button>
        ) : (
          <button
            type="button"
            onClick={onSignIn}
            className="mt-2 text-biddle-blue hover:underline"
          >
            Sign in
          </button>
        )}
      </div>
    )
  }

  function SidebarBody({ showClose }: { showClose: boolean }) {
    return (
      <>
        <div
          className={cn(
            'flex shrink-0 items-center justify-between',
            isDesktop ? 'px-7 py-7' : 'px-6 py-6',
          )}
        >
          <NavLink to="/search" className="block" aria-label="Biddle home">
            <img
              src={biddleLogoWhite}
              alt="Biddle"
              className={cn('w-auto', isDesktop ? 'h-11' : 'h-8')}
            />
          </NavLink>
          {showClose ? (
            <button
              type="button"
              onClick={() => setNavOpen(false)}
              className="rounded-lg p-2 text-white/70 hover:bg-white/10 hover:text-white"
              aria-label="Close menu"
            >
              <X className={isDesktop ? 'h-6 w-6' : 'h-5 w-5'} />
            </button>
          ) : null}
        </div>
        <nav
          className={cn(
            'flex min-h-0 flex-1 flex-col overflow-y-auto',
            isDesktop ? 'gap-1.5 px-4' : 'gap-1 px-3',
          )}
        >
          <NavLinks viaDrawer={!isDesktop} />
        </nav>
        <AccountFooter />
      </>
    )
  }

  const showDesktopFloatingSearchChrome = isDesktop && isSearchRoute
  const showDesktopPageHeader = isDesktop && !isSearchRoute && !navOpen
  const showHamburger = !navOpen

  return (
    <MapsProvider>
      <div className="flex h-dvh w-full overflow-hidden bg-white md:flex-row">
        {/* Desktop collapsible push rail — no light border (avoids hairline on Search photo). */}
        <aside
          className={cn(
            'hidden h-dvh shrink-0 flex-col overflow-hidden bg-navy text-white transition-[width] duration-300 ease-out md:flex',
            navOpen ? DESKTOP_RAIL_OPEN : 'w-0',
          )}
          aria-hidden={!navOpen}
        >
          <div className={cn('flex h-full flex-col', DESKTOP_RAIL_INNER)}>
            <SidebarBody showClose />
          </div>
        </aside>

        {/* Mobile overlay drawer */}
        {!isDesktop && drawerMounted ? (
          <div className="fixed inset-0 z-40 flex">
            <button
              type="button"
              className={cn(
                'absolute inset-0 bg-navy/50 backdrop-blur-sm transition-opacity duration-300 ease-out',
                drawerEntered ? 'opacity-100' : 'opacity-0',
              )}
              aria-label="Close menu"
              onClick={() => setNavOpen(false)}
            />
            <aside
              className={cn(
                'relative z-10 flex h-full w-64 flex-col bg-navy text-white shadow-2xl transition-transform duration-300 ease-out',
                drawerEntered ? 'translate-x-0' : '-translate-x-full',
              )}
            >
              <SidebarBody showClose />
            </aside>
          </div>
        ) : null}

        <div className="relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          {/* Mobile top bar */}
          <header className="sticky top-0 z-20 grid shrink-0 grid-cols-[1fr_auto_1fr] items-center border-b border-border bg-white/95 px-4 py-3 backdrop-blur md:hidden">
            <div className="justify-self-start">
              <button
                type="button"
                onClick={() => setNavOpen(true)}
                className="rounded-lg p-2 text-navy hover:bg-black/5"
                aria-label="Open menu"
              >
                <Menu className="h-5 w-5" />
              </button>
            </div>
            <NavLink to="/search" className="justify-self-center" aria-label="Biddle home">
              <img src={biddleLogo} alt="Biddle" className="h-7 w-auto" />
            </NavLink>
            <div className="flex items-center justify-self-end gap-1">
              {isLoading ? <Spinner className="h-4 w-4" /> : null}
              {!user ? (
                <button
                  type="button"
                  onClick={onSignIn}
                  className="rounded-full bg-biddle-blue px-3 py-1 text-xs font-semibold text-white transition hover:bg-biddle-blue-dark"
                >
                  Sign in
                </button>
              ) : null}
              {user && !isGuest ? (
                <button
                  type="button"
                  onClick={onOpenSettings}
                  className="rounded-lg p-2 text-muted hover:bg-surface"
                  aria-label="Open settings"
                >
                  <Settings className="h-5 w-5" />
                </button>
              ) : null}
            </div>
          </header>

          {/* Desktop non-Search header (in flow) when rail is closed */}
          {showDesktopPageHeader ? (
            <header className="sticky top-0 z-20 hidden shrink-0 items-center border-b border-border bg-white/95 px-4 py-3 backdrop-blur md:grid md:grid-cols-[1fr_auto_1fr]">
              <div className="justify-self-start">
                <button
                  type="button"
                  onClick={() => setNavOpen(true)}
                  className="rounded-lg p-2 text-navy hover:bg-black/5"
                  aria-label="Open menu"
                >
                  <Menu className="h-5 w-5" />
                </button>
              </div>
              <NavLink to="/search" className="justify-self-center" aria-label="Biddle home">
                <img src={biddleLogo} alt="Biddle" className="h-7 w-auto" />
              </NavLink>
              <div className="flex items-center justify-self-end gap-1">
                {isLoading ? <Spinner className="h-4 w-4" /> : null}
                {!user ? (
                  <button
                    type="button"
                    onClick={onSignIn}
                    className="rounded-full bg-biddle-blue px-3 py-1 text-xs font-semibold text-white transition hover:bg-biddle-blue-dark"
                  >
                    Sign in
                  </button>
                ) : null}
              </div>
            </header>
          ) : null}

          {/* Desktop Search: controls float over the photo — no top bar strip */}
          {showDesktopFloatingSearchChrome ? (
            <>
              {showHamburger ? (
                <button
                  type="button"
                  onClick={() => setNavOpen(true)}
                  className="absolute left-4 top-4 z-20 hidden rounded-lg p-2 text-white drop-shadow-sm transition hover:bg-white/15 md:block"
                  aria-label="Open menu"
                >
                  <Menu className="h-6 w-6" />
                </button>
              ) : null}
              <div className="absolute right-4 top-4 z-20 hidden items-center gap-2 md:flex">
                {isLoading ? <Spinner className="h-4 w-4" /> : null}
                {!user ? (
                  <button
                    type="button"
                    onClick={onSignIn}
                    className="rounded-full bg-biddle-blue px-3 py-1 text-xs font-semibold text-white transition hover:bg-biddle-blue-dark"
                  >
                    Sign in
                  </button>
                ) : null}
              </div>
            </>
          ) : null}

          <main
            className={cn(
              'relative min-h-0 flex-1',
              isSearchRoute
                ? 'overflow-hidden p-0'
                : isMapRoute
                  ? 'overflow-hidden p-0'
                  : isChatRoute
                    ? 'flex flex-col overflow-hidden px-4 py-4 md:px-8 md:py-6'
                    : 'overflow-y-auto px-4 py-4 md:px-8 md:py-8',
            )}
          >
            {mapMounted ? (
              <div
                className={cn(
                  'h-full min-h-0',
                  !isMapRoute && 'pointer-events-none invisible absolute inset-0 -z-10',
                )}
                aria-hidden={!isMapRoute}
              >
                <MapPage active={isMapRoute} />
              </div>
            ) : null}
            {!isMapRoute ? <Outlet /> : null}
          </main>
        </div>

        <PropertyProfileModal />
        <BiddleDetailModal />
      </div>
    </MapsProvider>
  )
}
